import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useAuthStore } from "./stores/authStore.ts";
import { useSettingsStore } from "./stores/settingsStore.ts";
import { getInitDataWithRetry } from "./telegram/init.ts";
import { AppErrorBoundary } from "./components/AppErrorBoundary.tsx";
import { QueryStatusBanner } from "./components/QueryStatusBanner.tsx";

const Home = lazy(() => import("./pages/Home.tsx"));
const Portfolio = lazy(() => import("./pages/Portfolio.tsx"));
const Expenses = lazy(() => import("./pages/Expenses.tsx"));
const Subscriptions = lazy(() => import("./pages/Subscriptions.tsx"));
const AddSubscription = lazy(() => import("./pages/AddSubscription.tsx"));
const EditSubscription = lazy(() => import("./pages/EditSubscription.tsx"));
const Settings = lazy(() => import("./pages/Settings.tsx"));
const Currencies = lazy(() => import("./pages/Currencies.tsx"));
const Connections = lazy(() => import("./pages/connections/Connections.tsx"));
const GooglePage = lazy(() => import("./pages/connections/Google.tsx"));
const Exchanges = lazy(() => import("./pages/connections/Exchanges.tsx"));
const AddExchangePage = lazy(() => import("./pages/connections/AddExchange.tsx"));
const GooglePlayPage = lazy(() => import("./pages/connections/GooglePlay.tsx"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
      staleTime: 30_000,
    },
  },
});

function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center text-tg-hint">Loading…</div>
  );
}

const normalizePathname = (pathname: string) => {
  const collapsedSlashes = pathname.replace(/\/{2,}/g, "/");
  if (collapsedSlashes === "/") {
    return collapsedSlashes;
  }
  return collapsedSlashes.replace(/\/+$/, "");
};

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  const { t } = useTranslation();
  if (!isAuthenticated)
    return <div className="p-4 text-center">{t('errors.authenticating')}</div>;
  return <>{children}</>;
}

function AppContent() {
  const { t } = useTranslation();
  const { authenticate, isAuthenticating, error, isAuthenticated } =
    useAuthStore();
  const { theme, systemDark, setSystemDark } = useSettingsStore();
  const isDark = theme === 'dark' || (theme === 'system' && systemDark);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
    const color = isDark ? '#141820' : '#F4F5F8';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', color);
    try {
      window.Telegram?.WebApp?.setHeaderColor(color);
      window.Telegram?.WebApp?.setBackgroundColor(color);
    } catch {
      // Older Telegram hosts may not support host color changes.
    }
  }, [isDark]);

  useEffect(() => {
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    const telegram = window.Telegram?.WebApp;
    const update = () => setSystemDark(telegram?.colorScheme === 'dark' || (!telegram && Boolean(media?.matches)));
    update();
    telegram?.onEvent?.('themeChanged', update);
    media?.addEventListener('change', update);
    return () => {
      telegram?.offEvent?.('themeChanged', update);
      media?.removeEventListener('change', update);
    };
  }, [setSystemDark]);

  useEffect(() => {
    const normalizedPathname = normalizePathname(window.location.pathname);
    if (normalizedPathname !== window.location.pathname) {
      const normalizedUrl = `${normalizedPathname}${window.location.search}${window.location.hash}`;
      window.history.replaceState(window.history.state, "", normalizedUrl);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const runAuth = async () => {
      const initData = await getInitDataWithRetry();

      if (!mounted) {
        return;
      }

      if (!initData) {
        console.error("");
        console.error(
          "═══════════════════════════════════════════════════════",
        );
        console.error("❌ ОШИБКА: InitData отсутствует!");
        console.error(
          "═══════════════════════════════════════════════════════",
        );
        console.error("");
        console.error("Приложение открыто в Telegram, но без initData.");
        console.error("");
        console.error("🔧 КАК ИСПРАВИТЬ (2 минуты):");
        console.error("");
        console.error("1. Откройте @BotFather в Telegram");
        console.error("2. Отправьте: /mybots");
        console.error("3. Выберите вашего бота");
        console.error("4. Bot Settings → Menu Button");
        console.error("5. Configure Menu Button");
        console.error("6. Введите URL: https://tma.tarifdata.com");
        console.error("7. Закройте и снова откройте бота");
        console.error("8. Нажмите кнопку ☰ внизу чата");
        console.error("");
        console.error(
          "═══════════════════════════════════════════════════════",
        );
        console.error("");
        useAuthStore.setState({
          error: "Menu Button не настроен",
          isAuthenticating: false,
        });
        return;
      }

      await authenticate(initData);
    };

    void runAuth();

    return () => {
      mounted = false;
    };
  }, [authenticate]);

  // Show setup instructions if no initData
  if (
    !isAuthenticating &&
    !isAuthenticated &&
    error === "Menu Button не настроен"
  ) {
    // Get diagnostic info
    const tg = typeof window !== "undefined" && window.Telegram?.WebApp;
    const diagnosticInfo = tg
      ? {
          version: tg.version,
          platform: tg.platform,
          url: window.location.href,
          hasStartParam: new URLSearchParams(window.location.search).has(
            "tgWebAppStartParam",
          ),
        }
      : null;

    return (
      <div className="flex justify-center items-center p-4 h-screen bg-tg-bg">
        <div className="max-w-md text-center">
          <div className="mb-4 text-6xl">⚙️</div>
          <h1 className="mb-4 text-xl font-bold text-tg-text">
            {t('errors.setupRequired')}
          </h1>

          <div className="p-4 mb-6 space-y-3 text-sm text-left rounded-lg text-tg-text bg-tg-secondary-bg">
            <p>
              Приложение должно быть открыто через <strong>Menu Button</strong>{" "}
              бота.
            </p>

            {diagnosticInfo && (
              <div className="p-2 text-xs bg-opacity-10 rounded bg-tg-hint">
                <p className="mb-1 font-bold">Диагностика:</p>
                <p>
                  Telegram: {diagnosticInfo.version} ({diagnosticInfo.platform})
                </p>
                <p className="break-all">URL: {diagnosticInfo.url}</p>
                <p>StartParam: {diagnosticInfo.hasStartParam ? "Да" : "Нет"}</p>
              </div>
            )}

            <div>
              <p className="mb-2 font-bold">Настройка (2 минуты):</p>
              <ol className="space-y-1 text-xs list-decimal list-inside">
                <li>
                  Откройте <strong>@BotFather</strong>
                </li>
                <li>
                  Отправьте:{" "}
                  <code className="px-1 bg-opacity-20 rounded bg-tg-hint">
                    /mybots
                  </code>
                </li>
                <li>Выберите вашего бота</li>
                <li>
                  <strong>Bot Settings</strong> → <strong>Menu Button</strong>
                </li>
                <li>
                  <strong>Configure Menu Button</strong>
                </li>
                <li>
                  URL:{" "}
                  <code className="px-1 text-xs break-all bg-opacity-20 rounded bg-tg-hint">
                    https://tma.tarifdata.com
                  </code>
                </li>
                <li>Закройте и откройте бота заново</li>
                <li>
                  Нажмите кнопку <strong>☰</strong> внизу
                </li>
              </ol>
            </div>

            <div className="p-2 text-xs bg-tg-warning bg-opacity-20 rounded">
              <p className="mb-1 font-bold">⚠️ Важно:</p>
              <p>НЕ открывайте по ссылке! Только через кнопку ☰ в боте.</p>
            </div>
          </div>

          <button
            onClick={() => window.location.reload()}
            className="px-4 py-3 w-full font-medium rounded-lg bg-tg-button text-tg-button-text"
          >
            {t('errors.reload')}
          </button>

          <p className="mt-4 text-xs text-tg-hint">
            {t('errors.consoleDetails')}
          </p>
        </div>
      </div>
    );
  }

  if (isAuthenticating) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="mb-4">{t('errors.authenticating')}</div>
          {error && <div className="text-sm text-tg-error">{error}</div>}
        </div>
      </div>
    );
  }

  if (error && !isAuthenticating) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="p-4 max-w-md text-center">
          <div className="mb-4 text-sm text-tg-error">{error}</div>

          <div className="p-3 mb-4 text-xs text-left text-tg-hint bg-tg-secondaryBg rounded">
            <p className="mb-2 font-bold">Как исправить:</p>
            <ol className="space-y-1 list-decimal list-inside">
              <li>Откройте @BotFather в Telegram</li>
              <li>Выберите вашего бота</li>
              <li>Bot Settings → Menu Button</li>
              <li>Установите URL: https://tma.tarifdata.com</li>
              <li>Откройте бота и нажмите Menu Button</li>
            </ol>
          </div>

          <button
            onClick={async () => {
              const initData = await getInitDataWithRetry();
              if (initData) {
                await authenticate(initData);
              }
            }}
            className="px-4 py-2 text-tg-buttonText bg-tg-button rounded hover:opacity-90"
          >
            {t('errors.tryAgain')}
          </button>

          <button
            onClick={() => {
              console.log("Opening browser console for debug info...");
              console.log(
                "Check the console above for detailed debug information",
              );
            }}
            className="px-4 py-2 mt-2 text-xs text-tg-bg bg-tg-hint rounded hover:bg-tg-hint"
          >
            Показать отладочную информацию
          </button>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
        <div className="min-h-screen bg-tg-bg text-tg-text">
          <Routes>
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Home />
                </ProtectedRoute>
              }
            />
            <Route
              path="/portfolio"
              element={
                <ProtectedRoute>
                  <Portfolio />
                </ProtectedRoute>
              }
            />
            <Route path="/expenses" element={<ProtectedRoute><Expenses /></ProtectedRoute>} />
            <Route
              path="/subscriptions"
              element={
                <ProtectedRoute>
                  <Subscriptions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-subscription"
              element={
                <ProtectedRoute>
                  <AddSubscription />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-subscription/:id"
              element={
                <ProtectedRoute>
                  <EditSubscription />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/currencies"
              element={
                <ProtectedRoute>
                  <Currencies />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/connections"
              element={
                <ProtectedRoute>
                  <Connections />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/connections/google"
              element={
                <ProtectedRoute>
                  <GooglePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/connections/exchanges"
              element={
                <ProtectedRoute>
                  <Exchanges />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/connections/exchanges/add"
              element={
                <ProtectedRoute>
                  <AddExchangePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/connections/google-play"
              element={
                <ProtectedRoute>
                  <GooglePlayPage />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
    </BrowserRouter>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppErrorBoundary>
        <QueryStatusBanner />
        <Suspense fallback={<RouteFallback />}>
          <AppContent />
        </Suspense>
      </AppErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
